/**
 * Utility functions for Enhanced Dashboard
 */

import { VALUATION_METHOD_MAP, SCORE_THRESHOLDS } from './enhancedDashboardConstants';

/**
 * Format a number as currency
 */
export const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

/**
 * Format a number as percentage
 */
export const formatPercentage = (value) => {
  if (!value && value !== 0) return '0%';
  return `${Math.round(value)}%`;
};

/**
 * Get Tailwind CSS text color class based on score
 */
export const getScoreColor = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return 'text-[#8b6f47]';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return 'text-green-600';
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return 'text-blue-600';
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return 'text-yellow-600';
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return 'text-orange-600';
  return 'text-red-600';
};

/**
 * Get Tailwind CSS background color class based on score
 */
export const getScoreBgColor = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return 'bg-[#f5e6d3]';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return 'bg-green-50';
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return 'bg-blue-50';
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return 'bg-yellow-50';
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return 'bg-orange-50';
  return 'bg-red-50';
};

/**
 * Get Tailwind CSS border color class based on score
 */
export const getScoreBorderColor = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return 'border-[#c49e73]';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return 'border-green-200';
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return 'border-blue-200';
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return 'border-yellow-200';
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return 'border-orange-200';
  return 'border-red-200';
};

/**
 * Get score label text
 */
export const getScoreLabel = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return 'No Gaps';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return 'Minor Gaps';
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return 'Considerable Gaps';
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return 'Critical Gaps';
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return 'Very Critical Gaps';
  return 'Extremely Critical';
};

/**
 * Get Tailwind CSS gradient classes based on score
 */
export const getScoreGradient = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return 'from-amber-600 to-yellow-600';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return 'from-green-600 to-emerald-600';
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return 'from-blue-600 to-cyan-600';
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return 'from-yellow-600 to-orange-500';
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return 'from-orange-600 to-red-500';
  return 'from-red-600 to-rose-700';
};

/**
 * Get human-readable valuation method name
 */
export const getValuationMethodName = (method) => {
  return VALUATION_METHOD_MAP[method?.toLowerCase()] || method || 'Multiple of EBITDA';
};
